import { getJsonPath, toFiniteNumber } from './jsonPath';

/** A single normalised data point fed to Recharts. We keep this
 *  shape minimal — Recharts is happy with arbitrary `{ name, ...numbers }`
 *  objects, but every chart in this folder only needs ONE label key
 *  ("name") plus N numeric series keyed by their `yKey`. */
export type ChartPoint = Record<string, string | number>;

/** Single "series" the chart should plot. `dataKey` is the property
 *  on the ChartPoint to read. `label` is the legend display name.
 *  `color` overrides the default rotation when set. */
export type ChartSeries = {
  dataKey: string;
  label?: string;
  color?: string;
};

/* Bosch-palette-aligned defaults. Reused across bar / line / pie so
 * a page that mixes chart types still reads as one visual system.
 * Sourced from the Bosch design tokens we already import elsewhere
 * — we hard-code hex here to keep this util import-free of the
 * theme module (avoiding a circular dependency once we centralise
 * theme tokens). */
export const DEFAULT_SERIES_COLORS = [
  '#0079B0', // boschBlue.50
  '#00884A', // boschGreen.40
  '#B0383D', // boschRed.40
  '#9E2896', // boschPurple.40
  '#9B7800', // boschYellow.40
  '#6CB1D6', // boschBlue.60
  '#3CAF7B', // boschGreen.50
];

export type BuildChartPointsArgs = {
  /** The array from the API. Should already be array-shaped. */
  rows: unknown[];
  /** Dot path on each row pointing at the label (X axis / pie slice). */
  xKey: string;
  /** One or more numeric dot paths on each row, one per series. */
  series: ChartSeries[];
};

/**
 * Turn an arbitrary JSON array into the `{ name, <series>: number }[]`
 * shape Recharts wants.
 *
 * Failure modes are quiet — a row with a missing y-value is rendered
 * as a gap in line charts and a zero-height bar in bar charts, which
 * is the same behaviour we get when the upstream API legitimately
 * returns no data for a slot. Throwing here would prevent any other
 * series from rendering for the same timestamp / label.
 */
export const buildChartPoints = ({
  rows,
  xKey,
  series,
}: BuildChartPointsArgs): ChartPoint[] => {
  if (!Array.isArray(rows)) return [];
  return rows.map((row, idx) => {
    // Use the index as a fallback label so the chart still renders
    // SOMETHING when the admin forgets to set xKey. The label looks
    // like "#0" / "#1" — clearly debug-ish, prompting them to fix
    // the config rather than silently rendering identical-looking
    // bars labelled "undefined".
    const rawX = getJsonPath(row, xKey);
    const point: ChartPoint = {
      name:
        rawX === undefined || rawX === null
          ? `#${idx}`
          : String(rawX),
    };
    for (const s of series) {
      const n = toFiniteNumber(getJsonPath(row, s.dataKey));
      // Skip non-finite values rather than coercing to 0 — a chart
      // with phantom zero-values for missing readings is worse than
      // a chart with a gap that visibly says "we don't have a
      // measurement here".
      if (n !== undefined) point[s.dataKey] = n;
    }
    return point;
  });
};

/** Returns the rendering colour for series `i`, rotating through
 *  the default palette when the admin didn't pin a custom colour. */
export const seriesColor = (
  series: ChartSeries,
  index: number,
): string => {
  if (series.color) return series.color;
  return DEFAULT_SERIES_COLORS[index % DEFAULT_SERIES_COLORS.length];
};
